/**
 * Choosing something already uploaded, for an item's video or one of its artwork roles. Videos
 * still on their way can be chosen too (the item shows their state until they are ready); images
 * are offered for the role being filled.
 */

import { useMemo, useState } from "react";
import Empty from "@web-ui/components/Empty";
import Field from "@web-ui/components/Field";
import Modal from "@web-ui/components/Modal";
import type { MediaAsset } from "@/api/admin";
import { lengthWords } from "@/forms/time";
import { useUploads } from "./upload-center";
import { UploadStateChip } from "./upload-state";
import { videoState } from "./states";
import { ROLE_WORDS } from "./image-upload";
import type { ImageRole } from "@media-domain/images";

export function MediaPicker({ kind, role, open, onClose, onPick }: { kind: "video" | "image"; role?: string; open: boolean; onClose: () => void; onPick: (asset: MediaAsset) => void }) {
  const { assets } = useUploads();
  const [q, setQ] = useState("");
  const shown = useMemo(() => (assets ?? [])
    .filter((a) => a.kind === kind && a.status !== "failed" && (kind === "video" || (a.status === "ready" && (!role || (a.details as { role?: string } | null)?.role === role))))
    .filter((a) => !q.trim() || a.title.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => b.createdAt - a.createdAt), [assets, kind, role, q]);
  const what = kind === "video" ? "a video" : `a ${role ? ROLE_WORDS[role as ImageRole]?.toLowerCase() ?? role : "picture"}`;
  return (
    <Modal open={open} onClose={onClose} size="lg">
      <div className="flex flex-col gap-4">
        <h2 className="text-h5">Choose {what}</h2>
        <Field aria-label="Search files" size="sm" placeholder="Search by name" value={q} onChange={(e) => setQ(e.target.value)} />
        {assets === null ? <p className="text-body-2 text-t-secondary">Loading your files…</p> : null}
        {assets && shown.length === 0 ? <Empty icon={kind === "video" ? "video" : "camera"} title={q ? "Nothing matches" : `No ${kind === "video" ? "videos" : "pictures of this shape"} yet`} description={q ? "Try another name." : "Close this and upload one instead."} /> : null}
        <ul className="grid max-h-[60vh] gap-2 overflow-auto sm:grid-cols-2">
          {shown.map((a) => {
            const thumb = (a.feed as { sizes?: Array<{ url: string }> } | null)?.sizes?.[0]?.url;
            const d = a.details as { durationSeconds?: number } | null;
            return (
              <li key={a.id}>
                <button type="button" className="flex w-full items-center gap-3 rounded-2xl bg-b-surface1 p-3 text-start transition-colors hover:bg-b-surface3 focus-visible:outline-2 focus-visible:outline-s-focus" onClick={() => { onPick(a); onClose(); }}>
                  {thumb ? <img src={thumb} alt="" className="h-12 w-12 shrink-0 rounded-lg object-cover" /> : <span className="h-12 w-12 shrink-0 rounded-lg bg-b-surface3" aria-hidden="true" />}
                  <span className="flex min-w-0 flex-col gap-1">
                    <span className="truncate text-body-2">{a.title}</span>
                    {kind === "video" ? (
                      <span className="flex flex-wrap items-center gap-2 text-caption text-t-tertiary">{a.status === "ready" ? lengthWords(d?.durationSeconds) : <UploadStateChip state={videoState(a)} />}</span>
                    ) : null}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </Modal>
  );
}
